import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SkeletonLoader from './SkeletonLoader';

const { FiEdit2, FiTrash2, FiPlus, FiFolder, FiCheckCircle } = FiIcons;

const ProjectList = ({ projects, tasks, onEditProject, onDeleteProject, onSelectProject, onAddProject, isLoading = false }) => {
  const { t } = useTranslation();
  const [particles, setParticles] = useState([]);

  const handleCreateClick = () => {
    const newParticles = Array.from({ length: 12 }).map((_, i) => {
      const edge = Math.floor(Math.random() * 4);
      const distance = 80 + Math.random() * 40;
      switch (edge) {
        case 0: // top
          return {
            id: i,
            left: `${Math.random() * 100}%`,
            top: '0%',
            x: (Math.random() - 0.5) * 40,
            y: -distance,
          };
        case 1: // right
          return {
            id: i,
            left: '100%',
            top: `${Math.random() * 100}%`,
            x: distance,
            y: (Math.random() - 0.5) * 40,
          };
        case 2: // bottom
          return {
            id: i,
            left: `${Math.random() * 100}%`,
            top: '100%',
            x: (Math.random() - 0.5) * 40,
            y: distance,
          };
        default: // left
          return {
            id: i,
            left: '0%',
            top: `${Math.random() * 100}%`,
            x: -distance,
            y: (Math.random() - 0.5) * 40,
          };
      }
    });
    setParticles(newParticles);
    setTimeout(() => {
      setParticles([]);
      if (onAddProject) onAddProject();
    }, 300);
  };

  // Count tasks in each project
  const getProjectStats = (projectId) => {
    const projectTasks = tasks.filter(task => task.projectId === projectId);
    const totalTasks = projectTasks.length;
    const completedTasks = projectTasks.filter(task => task.completed).length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    return { totalTasks, completedTasks, completionRate };
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-4">
        <div className="relative mb-3">
          <motion.button
            onClick={handleCreateClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 px-6 text-xl font-semibold text-white bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg shadow-lg flex items-center justify-center gap-2"
          >
            <SafeIcon icon={FiPlus} />
            {t('projects.createProject')}
          </motion.button>
          <AnimatePresence>
            {particles.map(p => (
              <motion.span
                key={p.id}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full pointer-events-none"
                style={{ left: p.left, top: p.top }}
                initial={{ opacity: 1, scale: 1 }}
                animate={{ opacity: 0, scale: 0, x: p.x, y: p.y }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </AnimatePresence>
        </div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{t('projects.title')}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t('projects.subtitle')}</p>
      </div>

      {isLoading ? (
        <SkeletonLoader variant="project" count={4} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence>
            {projects.map((project, index) => {
            const stats = getProjectStats(project.id);
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 hover:shadow-medium transition-shadow overflow-hidden"
                style={{ borderLeft: `4px solid ${project.color}` }}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: project.color }}
                      >
                        <SafeIcon icon={FiFolder} className="text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{project.name}</h3>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <motion.button
                        onClick={() => onEditProject(project)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        disabled={project.id === 'default'}
                        className={`p-2 rounded-lg transition-colors ${
                          project.id === 'default'
                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                            : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                        }`}
                        title={project.id === 'default' ? t('projects.inboxCannotEdit') : t('projects.editTooltip')}
                      >
                        <SafeIcon icon={FiEdit2} />
                      </motion.button>
                      <motion.button
                        onClick={() => onDeleteProject(project.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        disabled={project.id === 'default'}
                        className={`p-2 rounded-lg transition-colors ${
                          project.id === 'default'
                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                            : 'text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        title={project.id === 'default' ? t('projects.inboxCannotDelete') : t('projects.deleteTooltip')}
                      >
                        <SafeIcon icon={FiTrash2} />
                      </motion.button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {t('projects.tasksCompleted', { completed: stats.completedTasks, total: stats.totalTasks })}
                      </span>
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {stats.completionRate}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                        style={{ width: `${stats.completionRate}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* View Button */}
                  <motion.button
                    onClick={() => onSelectProject(project.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-2 px-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <SafeIcon icon={FiCheckCircle} />
                    {t('projects.viewTasks')}
                  </motion.button>
                </div>
              </motion.div>
            );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ProjectList;